/**
 * The roles the requirements define, and what each may do — the demo's own permission model,
 * which governs nothing live.
 *
 * survey-api's role model (SR-228) is live: an account's kind and its Team Portal roles, bundling
 * permissions, guard every live screen and every live rail entry, and are edited on Setup → Users
 * & roles → Roles. The mock screens have no route and so no permission there; which of them a
 * role opens is drawn here instead, as a capability matrix read by **"View as role"** in the guide
 * alone — which changes which mock screens and mock rail entries a viewer sees, and which of their
 * controls act, and says it changes nothing live.
 *
 * **The roles are survey-api's seeded codes** (its spec §8) — `analyst`, `compliance`,
 * `data_team`, `survey_owner`, `system_admin` — plus `advisor`, so one vocabulary names a role on
 * both sides; the labels are the seeded names. The capabilities are PRS §5's rights, grouped by
 * the screens that need them; where §5 says "all rights above", the grant lists them.
 */

export type DemoRole =
  | 'analyst'
  | 'compliance'
  | 'data_team'
  | 'survey_owner'
  | 'system_admin'
  | 'advisor';

export const DEMO_ROLES: readonly DemoRole[] = [
  'analyst',
  'compliance',
  'data_team',
  'survey_owner',
  'system_admin',
  'advisor'
];

export const DEMO_ROLE_LABEL: Record<DemoRole, string> = {
  analyst: 'Research analyst',
  compliance: 'Compliance reviewer',
  data_team: 'Data team',
  survey_owner: 'Survey Owner',
  system_admin: 'System administrator',
  advisor: 'Advisor'
};

export type Capability =
  | 'records'
  | 'lists'
  | 'compliance'
  | 'data'
  | 'placements'
  | 'survey-config'
  | 'own-survey';

export type RoleMatrix = Record<DemoRole, readonly Capability[]>;

const ANALYST: readonly Capability[] = ['records', 'lists'];

export const ROLE_MATRIX: RoleMatrix = {
  analyst: ANALYST,
  compliance: [...ANALYST, 'compliance'],
  data_team: [...ANALYST, 'data', 'placements'],
  survey_owner: [...ANALYST, 'compliance', 'data', 'placements', 'survey-config'],
  system_admin: ['records'],
  advisor: ['own-survey']
};

/**
 * Every mock screen and mock panel, and the capability it takes to open one and to act on it.
 * A `Record`, so a mock without a rule is a compile error — the step table's reason.
 */
export type MockScreen =
  | 'help-contact'
  | 'initial-review'
  | 'rankings';

export interface IMockAccess {
  /** Null: anyone who reaches the screen opens it — Help's contact block is read signed out. */
  opens: Nullable<Capability>;
  /** What its controls take, where that is more than opening it. */
  acts?: Capability;
}

export const MOCK_ACCESS: Record<MockScreen, IMockAccess> = {
  'help-contact': { opens: null },
  'initial-review': { opens: 'lists', acts: 'survey-config' },
  rankings: { opens: 'records', acts: 'placements' }
};

export function holds(role: DemoRole, capability: Capability): boolean {
  return ROLE_MATRIX[role].includes(capability);
}
